import type { Breakpoint, Theme } from "@mui/material/styles";
import type { CSSObject } from "@mui/system";

/**
 * PM5 — shared v4→v5+ parity primitives for converted files.
 *
 * Everything here restores a v4 rendering that a mechanical port loses. Nothing
 * here is a design decision: each export is the literal CSS v4 emitted, or the
 * literal cascade position v4's JSS sheet index bought. Mechanism write-ups
 * live in [`docs/mui-conversion-guide.md`](../../docs/mui-conversion-guide.md)
 * §2; this module exists so the compensations are stated once instead of being
 * re-derived (and mis-derived) per file.
 *
 * Sibling constants already inlined in earlier T2 batches (`v4GridContainerSx`,
 * `v4GridItemSx`, per-file `v4GridContainerWidthSx`) are byte-equivalent to the
 * helpers below; they are left where they are rather than churned, because
 * re-pointing them changes no rendering and every edit to a converted file is
 * a chance to change one.
 */

/* ------------------------------------------------------------------------ *
 * `<Grid container>` lost its unconditional `width: 100%`
 * ------------------------------------------------------------------------ */

/**
 * PARITY RESTORE (guide §2, "`<Grid container>` lost its `width: 100%`").
 *
 * v4: `.MuiGrid-container { box-sizing: border-box; display: flex;
 *      flex-wrap: wrap; **width: 100%** }` — every container, unconditionally.
 * v9: `generateGridStyles` emits `minWidth`, `boxSizing`, `display`,
 *      `flexWrap` and `gap` only. There is **no width rule at all**, so the
 *      element falls back to `width: auto`.
 *
 * A no-op while the container is block-level (`auto` == `100%` for a
 * border-box block). It is **not** a no-op when the container's rendered
 * parent is a flex container — the container is then a flex item, and
 * `width: auto` shrinks it to fit its text (measured 800px → 32px in
 * Chromium). The rendered parent can be in another file, so this is applied
 * on the container itself rather than reasoned about per call site.
 *
 * A container that already carries the v4 *spacing* width
 * (`calc(100% + …)`, below) needs nothing extra — that declaration superseded
 * the 100% in v4 too.
 */
export const v4GridContainerWidthSx = { width: "100%" } as const;

/* ------------------------------------------------------------------------ *
 * `<Grid container spacing={n}>` geometry
 * ------------------------------------------------------------------------ */

/**
 * PARITY RESTORE (guide §2, "Grid `spacing` implementation" / "…on a container
 * that styles its own box").
 *
 * v4's `generateGutter` (`@material-ui/core/Grid/Grid.js:87`) emitted, for
 * `spacing={n}` on this theme (`spacing: 8`):
 *
 *   `.MuiGrid-spacing-xs-n              { margin: -4n px; width: calc(100% + 8n px) }`
 *   `.MuiGrid-spacing-xs-n > .MuiGrid-item { padding: 4n px }`
 *
 * v9 implements `spacing` as `gap` on the container instead: items carry no
 * padding and the container no longer overhangs its parent. That is only
 * geometrically identical when nothing else touches the container's box, so
 * the repo ports v4's own declarations verbatim — omit `spacing` (v9 default
 * 0 ⇒ `gap: 0px`), put `v4GridSpacingSx(n).container` on the container and
 * `v4GridSpacingSx(n).item` on each item. Identical by construction rather
 * than by arithmetic, and an inline `style` still overrides one half of the
 * `margin` shorthand exactly as it did in v4.
 */
export const v4GridSpacingSx = (
  n: number,
): { container: CSSObject; item: CSSObject } => ({
  container: {
    width: `calc(100% + ${8 * n}px)`,
    margin: `-${4 * n}px`,
  },
  item: { padding: `${4 * n}px` },
});

/* ------------------------------------------------------------------------ *
 * A flat `sx` declaration no longer beats the component's own @media rule
 * ------------------------------------------------------------------------ */

/**
 * PARITY RESTORE (guide §2, "A flat `sx` declaration loses to the component's
 * own breakpoint rule").
 *
 * v4: a file's `makeStyles` class was a **separate** class from MUI's own, and
 * JSS gave every `makeStyles` sheet a higher index than the library's — so a
 * FLAT declaration in the file's class (`padding: 0`) beat the component's
 * breakpoint-scoped declaration (`@media (min-width:600px) { padding-left:
 * 24px }`) at **every** width. Both are (0,1,0); the later sheet won.
 *
 * v9: `sx` merges into the **same** emotion class as the component's root
 * styles and `styleOverrides`, and stylis hoists at-rule blocks **after** that
 * class's own flat declarations — measured on the emitted CSS:
 *
 *   `.css-x{…;padding-left:16px;padding-right:16px;padding:0;}`
 *   `@media (min-width:600px){.css-x{padding-left:24px;padding-right:24px;}}`
 *
 * so above the breakpoint the component's rule wins and the file's override
 * silently stops applying. (Measured cost on the T2 visual gate: every
 * `<Container>` whose v4 class cancelled the gutters regained 48px of
 * horizontal padding at ≥600px — login card 460→412px, signup card 600→552px.)
 *
 * The restore is to re-state the same declarations inside each breakpoint the
 * component itself declares them at. The `sx` media block is emitted after the
 * component's, so it wins on order at equal specificity — exactly what the v4
 * sheet index bought, and emitting nothing v4 did not emit.
 *
 * Any breakpoint block the caller already declares itself wins over the
 * re-stated value: that block is v4's own responsive override, not collateral.
 *
 * @param theme  the theme, for `breakpoints.up()`
 * @param sx     the file's sx object, written exactly as v4's class was
 * @param props  the flat keys of `sx` the component also sets at a breakpoint
 * @param breakpoints  the breakpoints the component sets them at
 */
export const v4FlatWinsAtBreakpointsSx = (
  theme: Theme,
  sx: CSSObject,
  props: readonly string[],
  breakpoints: readonly Breakpoint[] = ["sm", "md"],
): CSSObject => {
  const restated: CSSObject = {};
  props.forEach((prop) => {
    if (prop in sx) restated[prop] = sx[prop] as CSSObject[string];
  });

  const out: CSSObject = { ...sx };
  breakpoints.forEach((bp) => {
    const key = theme.breakpoints.up(bp);
    const existing = out[key];
    out[key] = {
      ...restated,
      // the file's own breakpoint override is v4 behaviour — it stays on top
      ...(typeof existing === "object" && existing !== null ? existing : {}),
    } as CSSObject;
  });
  return out;
};
